import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

export default function TopsBySourceTable({ model, top }) {
  const { t } = useTranslation();

  //============================================================================================================
  // Prepare the data for the table
  //============================================================================================================
  const tableData = useMemo(() => {
    const sources = Array.from(model.quotesBySource.keys()); // Get unique sources from the quotesBySource map

    const stats = new Map(); // Map to hold stats for each carrier
    (model.normCarriers || []).forEach((c) => {
      // Initialize stats for each carrier
      stats.set(c.id, {
        // Initialize with empty values
        name: c.name,
        perSource: Object.fromEntries(sources.map((s) => [s, "0%"])),
        total: 0,
      });
    });

    sources.forEach((src) => {
      // For each source, calculate the stats
      const srcQuotes = model.quotesBySource.get(src) || []; // Get quotes for the current source
      const ref = model.topBySource.get(src) || { top1: null, topN: null }; // Get the reference values for top1 and topN
      const refValue = top === 1 ? ref.top1 : ref.topN; // Use the appropriate reference value based on the top parameter

      const byCarrier = new Map(); // Map to hold quotes by carrier for the current source
      srcQuotes.forEach((q) => {
        // Group quotes by carrier
        const cId = q.CarrierId ?? q.Carrier?.Id; // Use CarrierId or Carrier.Id
        if (cId == null) return; // Skip if no carrier ID is found
        const arr = byCarrier.get(cId) || []; // Get existing array or create a new one
        arr.push(q); // Add the quote to the array
        byCarrier.set(cId, arr); // Update the map with the new array
      });

      byCarrier.forEach((arr, cId) => {
        // For each carrier, calculate the matching quotes
        if (!stats.has(cId)) {
          // If the carrier is not already in stats, initialize it
          stats.set(cId, {
            // Initialize with empty values
            name: arr[0]?.CarrierName ?? String(cId), // Use CarrierName or fallback to CarrierId
            perSource: Object.fromEntries(sources.map((s) => [s, "0%"])), // Initialize percentage for each source
            total: 0, // Initialize total count
          });
        }
        const matching =
          refValue != null
            ? arr.filter((q) => q.BidValue <= refValue).length
            : 0; // Count matching quotes based on the reference value
        const pct = arr.length ? Math.round((matching / arr.length) * 100) : 0; // Calculate percentage of matching quotes

        const s = stats.get(cId); // Get the stats for the current carrier
        s.perSource[src] = `${pct}%`; // Update the percentage for the current source
        s.total += matching; // Update the total count for the carrier
      });
    });

    // Convert stats map to an array of rows
    return {
      sources,
      rows: (model.normCarriers || []).map((c) => {
        const s = stats.get(c.id) ?? {
          name: c.name,
          perSource: Object.fromEntries(sources.map((s) => [s, "0%"])),
          total: 0,
        };
        return { name: s.name, values: s.perSource, total: s.total };
      }),
    };
  }, [model, top]);

  //============================================================================================================
  // Render the table
  //============================================================================================================
  return (
    // Wrapper + inner scroll container to allow horizontal scrolling only on the table
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "inline-block",
          marginBottom: "10px",
        }}
      >
        {t("TopSource", { top })}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-source">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              {tableData.sources.map((src) => (
                <th
                  key={src}
                  style={headerCellStyle()}
                  className="tops-col-main"
                >
                  {t(src)}
                </th>
              ))}
              <th style={headerCellStyle()} className="tops-col-total">
                {t("Total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                {tableData.sources.map((src) => (
                  <td
                    key={src}
                    style={cellStyle({ background: "var(--color-white)" })}
                    className="tops-col-main tops-cell"
                  >
                    {row.values[src] || "0%"}
                  </td>
                ))}
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-total tops-cell"
                >
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

TopsBySourceTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesBySource: PropTypes.instanceOf(Map).isRequired,
    topBySource: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  top: PropTypes.oneOf([1, 2]).isRequired,
};
